<template>
  <div key="order-summary" class="order-summary">
    <h2 v-if="!isElementHidden(HIDDEN_ELEMENTS.ORDER_SUMMARY)" class="mb-2">
      {{ $t("components.checkout.orderSummary.title") }}
    </h2>

    <list boxed>
      <list-item v-if="!isElementHidden(HIDDEN_ELEMENTS.PRODUCT)">
        <template slot="left">
          <div class="order-summary__left">
            <span>
              {{ $t("components.checkout.orderSummary.product") }}
            </span>
          </div>
        </template>
        <div class="v-list-item__title pl-2">
          {{ product.title }}
        </div>
      </list-item>

      <list-item
        v-if="!isElementHidden(HIDDEN_ELEMENTS.AMOUNT)"
        :align-start="hasTrial"
      >
        <template slot="left">
          <div class="order-summary__left">
            <span>{{ $t("components.checkout.orderSummary.amount") }}</span>
          </div>
        </template>
        <div class="v-list-item__title pl-2">
          <div v-if="hasTrial" class="mb-1">
            {{ product.trial_period_days }}
            {{ $t("components.checkout.orderSummary.dayFree") }}
          </div>
          <div>
            {{ product.symbol || "$" }}{{ product.price }}
            {{ product.currency }}
            <template v-if="product.interval">
              {{ $t("components.checkout.orderSummary.orderPer") }}
              {{ productInterval(product.interval) }}
            </template>
          </div>
        </div>
      </list-item>

      <list-item
        :align-start="isGuest"
        no-padding-top
        no-padding-right
        class="py-1"
      >
        <template slot="left">
          <div :class="['order-summary__left', { 'mt-2': isGuest }]">
            <span>
              {{ $t("components.checkout.orderSummary.email") }}
            </span>
          </div>
        </template>
        <div class="v-list-item__title p-1">
          <template v-if="isGuest">
            <v-input
              :value="email"
              autofocus
              placeholder="Enter your email"
              type="email"
              name="email"
              :validator="$v.emailValidator"
              @input="val => $emit('input', val)"
            />
            <div
              v-if="!isExistingEmail"
              class="text-small d-flex align-items-center px-1 pt-1 mb-n1"
            >
              {{ $t("components.checkout.orderSummary.alreadyAMember") }}
              <v-button
                small
                ghost
                class="p-0 ml-1"
                @click="() => openAuthModal()"
              >
                {{ $t("components.checkout.orderSummary.login") }}
              </v-button>
            </div>
          </template>
          <template v-else-if="currentUser">
            <span class="pl-1">
              {{ email }}
            </span>
          </template>
        </div>

        <template v-if="isExistingEmail && isGuest" slot="row">
          <div class="w-100 pb-2">
            <div class="d-flex justify-content-center mb-2">
              <v-icon icon="warning" size="1.2rem" />
              <span class="text-muted text-small opacity-60">
                {{ $t("components.checkout.orderSummary.emailAlreadyExists") }}
              </span>
            </div>
            <v-button block small @click="() => openAuthModal()">
              {{ $t("components.checkout.orderSummary.login") }}
            </v-button>
          </div>
        </template>
      </list-item>

      <slot />
    </list>

    <slot name="after" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import { validationMixin } from "vuelidate"
import { required, email } from "vuelidate/lib/validators"

import EventBus, { EVENTS } from "@/lib/event-bus"

import debounce from "@/lib/debounce"
import { productInterval } from "@/lib/product-interval"
import { STAGES } from "@/lib/checkout-utils"
import { isPresent } from "@/lib/utils"
import { HIDDEN_ELEMENTS } from "@/lib/constants"

import { STAGES as AUTH_STAGES } from "@/components/AuthModal"

import List from "@/components/List"
import ListItem from "@/components/ListItem"
import vInput from "@/components/forms/vInput"

export default {
  mixins: [validationMixin],

  components: {
    List,
    ListItem,
    vInput
  },

  model: {
    prop: "email",
    event: "input"
  },

  props: {
    email: { type: String, default: "" },
    product: { type: Object, required: true },
    hasTrial: { type: Boolean, default: false }
  },

  data() {
    return {
      emailValidator: this.email,
      isExistingEmail: false,
      isFetchingUser: false,

      HIDDEN_ELEMENTS
    }
  },

  computed: {
    ...mapGetters("auth", ["isGuest", "currentUser"]),
    ...mapGetters("ui", ["isElementHidden"]),

    isValidEmail() {
      return (
        isPresent(this.currentUser) ||
        (isPresent(this.email) &&
          email(this.email) &&
          !this.isExistingEmail &&
          !this.isFetchingUser)
      )
    }
  },

  validations: {
    emailValidator: {
      email,
      required
    }
  },

  watch: {
    email: {
      immediate: true,
      handler: function() {
        this.emailValidator = this.email
        this.$v.emailValidator.$touch()

        debounce(this.handleCheckEmail, 200)
      }
    },

    isValidEmail: {
      immediate: true,
      handler: function() {
        this.$emit("change", {
          email: this.email,
          valid: this.isValidEmail
        })
      }
    }
  },

  methods: {
    productInterval,

    ...mapActions("auth", ["fetchUserAuth"]),

    handleCheckEmail() {
      this.isExistingEmail = false

      if (this.email && email(this.email)) {
        this.isFetchingUser = true

        // Ensure email is not changed while fetching
        const email = this.email

        this.fetchUserAuth({ email: this.email })
          .then(res => {
            if (this.email === email) {
              this.isExistingEmail = res.exists && !res.passwordless
            }
          })
          .finally(() => {
            this.isFetchingUser = false
          })
      }
    },

    reopenCheckout() {
      this.$router.push(STAGES.PAY)
    },

    openAuthModal() {
      this.$router.push({
        hash: AUTH_STAGES.INITIAL,
        query: { email: this.email }
      })

      EventBus.$once(EVENTS.POST_AUTH_FLOW, this.reopenCheckout)
      EventBus.$once(EVENTS.AUTH_ABORTED, () => {
        EventBus.$off(EVENTS.POST_AUTH_FLOW, this.reopenCheckout)

        this.reopenCheckout()
      })
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.order-summary {
  .order-summary__left {
    width: 5rem;
    min-width: 5rem;
    max-width: 6rem;
    flex-grow: 0;
    padding-top: 0;
    display: flex;
    align-items: center;
    height: 100%;

    span {
      font-size: $font-size--small;
      color: $color-ui--grey-60;
    }
  }

  .flex-shrink-0 {
    flex-shrink: 0;
  }
  .w-100 {
    width: 100%;
  }
  .v-list-item__center {
    flex-grow: 1;
  }

  .v-list__box {
    margin: 0;
    box-shadow: none;
    border: 1px solid $color-ui--grey-20;
  }
}
</style>
